/**
 * Utility functions for generating summaries from work item details
 */

/**
 * Generate a summary from work item details
 * @param {Object} workItem - Work item object
 * @returns {String} - Generated summary
 */
export const generateSummary = (workItem) => {
  // Extract basic information
  const title = workItem.title || '';
  const description = workItem.description || '';
  const workItemType = workItem.workItemType;
  const clientName = workItem.clientName || '';
  
  // Extract client prefix if present
  let clientPrefix = '';
  let cleanTitle = title;
  
  if (clientName) {
    clientPrefix = `[${clientName}] `;
  } else {
    // Try to extract client name from title
    const clientMatch = title.match(/^\[(.*?)\]\s*(.*)/);
    if (clientMatch) {
      clientPrefix = `[${clientMatch[1]}] `;
      // Use the rest of the title without the client prefix
      cleanTitle = clientMatch[2];
    }
  }
  
  // Extract key information from description
  const keyInfo = extractKeyInformation(description);
  
  // Extract reproduction steps if it's a bug
  let reproSteps = '';
  if (workItemType === 'Bug') {
    reproSteps = extractReproductionSteps(description);
  }
  
  // Format based on work item type
  let summary = '';
  if (workItemType === 'Bug') {
    // Check if title already starts with "Fixed" or similar
    if (!cleanTitle.match(/^(Fix|Fixed|Fixes|Fixing)/i)) {
      summary = `${clientPrefix}Fixes an issue where ${cleanTitle.toLowerCase()}`;
    } else {
      summary = `${clientPrefix}${cleanTitle}`;
    }
    
    // Add severity information if available
    if (workItem.severity) {
      if (workItem.severity === '1' || workItem.severity === '2') {
        summary += " (Critical issue)";
      } else if (workItem.severity === '3') {
        summary += " (Major issue)";
      }
    }
  } else if (workItemType === 'Feature' || workItemType === 'Product Backlog Item' || workItemType === 'User Story') {
    // Check if title already starts with "Added" or similar
    if (!cleanTitle.match(/^(Add|Added|Adds|Adding|Implement|Implemented|Implements|Implementing)/i)) {
      summary = `Adds ${cleanTitle.toLowerCase()}`;
    } else {
      summary = cleanTitle;
    }
  } else if (workItemType === 'Epic') {
    if (!cleanTitle.match(/^(Enhance|Enhanced|Enhances|Enhancing)/i)) {
      summary = `Enhances ${cleanTitle.toLowerCase()}`;
    } else {
      summary = cleanTitle;
    }
  }
  
  // Add key information and reproduction steps
  if (keyInfo) {
    summary += ` ${keyInfo}`;
  }
  
  if (reproSteps) {
    summary += ` ${reproSteps}`;
  }
  
  // Ensure the summary ends with a period
  if (!summary.endsWith('.')) {
    summary += '.';
  }
  
  return summary;
};

/**
 * Extract key information from description
 * @param {String} description - Work item description
 * @returns {String} - Extracted key information
 */
const extractKeyInformation = (description) => {
  if (!description) return '';
  
  // Extract sentences using regex
  const sentences = description.split(/(?<=[.!?])\s+/);
  
  // Filter out non-essential sentences
  const functionalSentences = sentences.filter(sentence => {
    // Skip sentences that look like user comments or metadata
    if (sentence.match(/I have tested|I found|I think|screenshot|attached|please|thank you/i)) {
      return false;
    }
    // Skip sentences with personal pronouns that suggest comments
    if (sentence.match(/\b(I|we|my|our)\b/i)) {
      return false;
    }
    // Skip sentences that are too short to be meaningful
    if (sentence.split(/\s+/).length < 3) {
      return false;
    }
    // Skip sentences that are likely part of reproduction steps
    if (sentence.match(/step \d|click on|navigate to|select the|enter the|go to/i)) {
      return false;
    }
    
    return true;
  });
  
  // Use up to 2 functional sentences for the description
  return functionalSentences.slice(0, 2).join(' ');
};

/**
 * Extract reproduction steps from description
 * @param {String} description - Work item description
 * @returns {String} - Extracted reproduction steps
 */
const extractReproductionSteps = (description) => {
  if (!description) return '';
  
  // Look for reproduction steps in the description
  const reproMatch = description.match(/steps to reproduce|repro steps|reproduction steps|steps:|how to reproduce/i);
  if (!reproMatch) return '';
  
  // Extract text after the reproduction steps header
  const startIdx = reproMatch.index;
  const reproText = description.substring(startIdx);
  
  // Split into sentences and filter
  const reproSentences = reproText.split(/(?<=[.!?])\s+/);
  const filteredRepro = reproSentences
    .slice(0, 5)  // Limit to first 5 sentences of repro steps
    .filter(sentence => {
      // Skip test evidence statements
      if (sentence.match(/I have tested|I found|I think|screenshot|attached|please|thank you/i)) {
        return false;
      }
      // Skip sentences with personal pronouns that suggest comments
      if (sentence.match(/\b(I|we|my|our)\b/i)) {
        return false;
      }
      // Skip sentences that are too short to be meaningful
      if (sentence.split(/\s+/).length < 3) {
        return false;
      }
      
      return true;
    });
  
  return filteredRepro.join(' ');
};
